import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ClearIcon from '@mui/icons-material/Clear';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { motion, AnimatePresence } from 'framer-motion';
import ProfileHeader from '../../components/gallery/ProfileHeader';
import ProfileHeaderSkeleton from '../../components/gallery/ProfileHeaderSkeleton';
import AccountNavButtons from '../../components/gallery/AccountNavButtons';
import MediaGrid, { itemKey } from '../../components/gallery/MediaGrid';
import MediaLightbox from '../../components/gallery/MediaLightbox';
import MediaTypeFilter, {
  type MediaTypeFilterValue,
} from '../../components/gallery/MediaTypeFilter';
import ExtractAllModal from '../../components/gallery/ExtractAllModal';
import EmptyState from '../../components/gallery/EmptyState';
import FollowListDialog from '../../components/gallery/FollowListDialog';
import { fetchProfileMedia, type FollowListKind } from '../../services/profileMedia';
import { parseProfileHandle, getImageExtension, sanitizeForFilename } from '../../services/mediaUtils';
import { downloadItemsAsZip, downloadMediaUrl } from '../../services/zipDownload';
import { shareHintMessage } from '../../services/fileSave';
import type { ProfileMediaItem, ProfileUser, ZipProgress } from '../../services/mediaContracts';

const matchesMediaFilter = (
  item: ProfileMediaItem,
  filter: MediaTypeFilterValue
): boolean => {
  if (filter === 'all') return true;
  if (filter === 'photo') return item.type === 'photo';
  // Videos filter includes animated GIFs — they're playable clips.
  return item.type === 'video' || item.type === 'gif';
};

/** Items per API page — larger batches = fewer round-trips while scrolling. */
const PAGE_SIZE = 40;
/** How far below the viewport to start fetching metadata (~1.5 screens). */
const PREFETCH_ROOT_MARGIN = '1200px 0px';
/** Keep prefetching while within this many viewport heights of the bottom. */
const PREFETCH_SCROLL_BUFFER = 1.5;
/** Start loading more when this many items from the end (grid or lightbox). */
const PREFETCH_ITEM_BUFFER = 16;

const GalleryPage: React.FC = () => {
  const [handleInput, setHandleInput] = useState('');
  const [profile, setProfile] = useState<ProfileUser | null>(null);
  const [items, setItems] = useState<ProfileMediaItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [zipProgress, setZipProgress] = useState<ZipProgress | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [extractOpen, setExtractOpen] = useState(false);
  const [followListKind, setFollowListKind] = useState<FollowListKind | null>(null);
  const [canAccountBack, setCanAccountBack] = useState(false);
  const [canAccountForward, setCanAccountForward] = useState(false);
  const [mediaFilter, setMediaFilter] = useState<MediaTypeFilterValue>('all');

  const scrollSentinelRef = useRef<HTMLDivElement | null>(null);
  const cursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef(false);
  const activeHandleRef = useRef('');
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  /** After a failed page fetch, wait until the sentinel leaves view before retrying. */
  const retryBlockedRef = useRef(false);
  /** Browser-style stack of accounts visited via search / followers / following. */
  const accountHistoryRef = useRef<{ stack: string[]; index: number }>({
    stack: [],
    index: -1,
  });

  const syncAccountNav = useCallback(() => {
    const { stack, index } = accountHistoryRef.current;
    setCanAccountBack(index > 0);
    setCanAccountForward(index >= 0 && index < stack.length - 1);
  }, []);

  const markExhausted = useCallback(() => {
    hasMoreRef.current = false;
    setHasMore(false);
    cursorRef.current = null;
    setCursor(null);
  }, []);

  const applyPageCursor = useCallback(
    (nextCursor: string | null, itemCount: number, usedCursor: string | null) => {
      const exhausted =
        itemCount === 0 ||
        !nextCursor ||
        (usedCursor != null && nextCursor === usedCursor);

      if (exhausted) {
        markExhausted();
        return;
      }

      cursorRef.current = nextCursor;
      setCursor(nextCursor);
      hasMoreRef.current = true;
      setHasMore(true);
    },
    [markExhausted]
  );

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    if (selected.size === 0 || viewerIndex != null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelected(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected.size, viewerIndex]);

  const keyFor = useCallback((item: ProfileMediaItem) => itemKey(item), []);

  const filterCounts = useMemo(() => {
    let video = 0;
    let photo = 0;
    for (const item of items) {
      if (item.type === 'photo') photo += 1;
      else video += 1;
    }
    return { all: items.length, video, photo };
  }, [items]);

  const filteredItems = useMemo(
    () => items.filter((item) => matchesMediaFilter(item, mediaFilter)),
    [items, mediaFilter]
  );

  const indexByKey = useMemo(() => {
    const map = new Map<string, ProfileMediaItem>();
    // Full timeline — selection survives filter changes.
    items.forEach((item) => {
      map.set(keyFor(item), item);
    });
    return map;
  }, [items, keyFor]);

  const setMediaFilterSafe = useCallback((next: MediaTypeFilterValue) => {
    setMediaFilter(next);
    setViewerIndex(null);
  }, []);

  const loadMore = useCallback(async () => {
    const handle = activeHandleRef.current;
    const usedCursor = cursorRef.current;
    if (
      !handle ||
      !hasMoreRef.current ||
      !usedCursor ||
      loadingMoreRef.current ||
      loadingRef.current
    ) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    setError(null);

    try {
      const page = await fetchProfileMedia(handle, usedCursor, PAGE_SIZE);
      setProfile((prev) => page.profile || prev);
      if (page.items.length > 0) {
        setItems((prev) => {
          const seen = new Set(prev.map((i) => `${i.tweetId}|${i.downloadUrl}`));
          const next = [...prev];
          for (const item of page.items) {
            const key = `${item.tweetId}|${item.downloadUrl}`;
            if (!seen.has(key)) {
              seen.add(key);
              next.push(item);
            }
          }
          return next.length === prev.length ? prev : next;
        });
      }
      applyPageCursor(page.nextCursor, page.items.length, usedCursor);
      retryBlockedRef.current = false;
    } catch (err) {
      retryBlockedRef.current = true;
      setError(err instanceof Error ? err.message : 'Failed to load more media');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [applyPageCursor]);

  /** If still near the bottom after a fetch, queue another page immediately. */
  const prefetchUntilBuffered = useCallback(() => {
    requestAnimationFrame(() => {
      if (
        retryBlockedRef.current ||
        !hasMoreRef.current ||
        !cursorRef.current ||
        loadingMoreRef.current ||
        loadingRef.current
      ) {
        return;
      }
      const doc = document.documentElement;
      const distanceToBottom =
        doc.scrollHeight - window.scrollY - window.innerHeight;
      if (distanceToBottom < window.innerHeight * PREFETCH_SCROLL_BUFFER) {
        void loadMore().then(() => prefetchUntilBuffered());
      }
    });
  }, [loadMore]);

  const loadMedia = async (rawHandle: string, append = false) => {
    const handle = parseProfileHandle(rawHandle);
    if (!handle) {
      setError('Enter a valid @handle or profile URL (not a post link)');
      return;
    }

    if (append) {
      await loadMore();
      return;
    }

    setLoading(true);
    loadingRef.current = true;
    setItems([]);
    setProfile(null);
    markExhausted();
    setSelected(new Set());
    setViewerIndex(null);
    setMediaFilter('all');
    setStatusMsg(null);
    setError(null);
    setFollowListKind(null);
    retryBlockedRef.current = false;
    activeHandleRef.current = handle;

    try {
      const page = await fetchProfileMedia(handle, null, PAGE_SIZE);
      setProfile(page.profile);
      setItems(page.items);
      applyPageCursor(page.nextCursor, page.items.length, null);
      if (page.nextCursor && page.items.length > 0) {
        prefetchUntilBuffered();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media');
      setItems([]);
      setProfile(null);
      markExhausted();
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  /** Open an account and push it onto the browse history (unless already current). */
  const visitAccount = (rawHandle: string) => {
    const handle = parseProfileHandle(rawHandle);
    if (!handle) {
      setError('Enter a valid @handle or profile URL (not a post link)');
      return;
    }

    const hist = accountHistoryRef.current;
    const current = hist.stack[hist.index];
    if (!current || current.toLowerCase() !== handle.toLowerCase()) {
      const stack = [...hist.stack.slice(0, hist.index + 1), handle];
      accountHistoryRef.current = { stack, index: stack.length - 1 };
      syncAccountNav();
    }

    setHandleInput(`@${handle}`);
    void loadMedia(handle, false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goAccountBack = () => {
    const hist = accountHistoryRef.current;
    if (hist.index <= 0) return;
    const nextIndex = hist.index - 1;
    const handle = hist.stack[nextIndex];
    accountHistoryRef.current = { ...hist, index: nextIndex };
    syncAccountNav();
    setHandleInput(`@${handle}`);
    void loadMedia(handle, false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goAccountForward = () => {
    const hist = accountHistoryRef.current;
    if (hist.index < 0 || hist.index >= hist.stack.length - 1) return;
    const nextIndex = hist.index + 1;
    const handle = hist.stack[nextIndex];
    accountHistoryRef.current = { ...hist, index: nextIndex };
    syncAccountNav();
    setHandleInput(`@${handle}`);
    void loadMedia(handle, false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const sentinel = scrollSentinelRef.current;
    if (!sentinel || !hasMore || !cursor || items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries[0]?.isIntersecting;
        if (!intersecting) {
          retryBlockedRef.current = false;
          return;
        }
        if (
          retryBlockedRef.current ||
          !hasMoreRef.current ||
          !cursorRef.current ||
          loadingMoreRef.current ||
          loadingRef.current
        ) {
          return;
        }
        void loadMore().then(() => prefetchUntilBuffered());
      },
      { rootMargin: PREFETCH_ROOT_MARGIN }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, cursor, items.length, loadMore, prefetchUntilBuffered]);

  useEffect(() => {
    if (!hasMore || !cursor || items.length === 0) return;

    let ticking = false;
    const onScroll = () => {
      if (
        ticking ||
        retryBlockedRef.current ||
        loadingMoreRef.current ||
        loadingRef.current ||
        !hasMoreRef.current ||
        !cursorRef.current
      ) {
        return;
      }
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (!hasMoreRef.current || !cursorRef.current || loadingMoreRef.current) {
          return;
        }
        const doc = document.documentElement;
        const distanceToBottom =
          doc.scrollHeight - window.scrollY - window.innerHeight;
        if (distanceToBottom < window.innerHeight * PREFETCH_SCROLL_BUFFER) {
          void loadMore().then(() => prefetchUntilBuffered());
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasMore, cursor, items.length, loadMore, prefetchUntilBuffered]);

  useEffect(() => {
    if (viewerIndex == null || !hasMore) return;
    if (
      viewerIndex >= filteredItems.length - PREFETCH_ITEM_BUFFER &&
      hasMoreRef.current &&
      cursorRef.current &&
      !loadingMoreRef.current &&
      !loadingRef.current
    ) {
      void loadMore().then(() => prefetchUntilBuffered());
    }
  }, [
    viewerIndex,
    filteredItems.length,
    hasMore,
    loadMore,
    prefetchUntilBuffered,
  ]);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    visitAccount(handleInput);
  };

  const openViewer = (_item: ProfileMediaItem, index: number) => {
    if (selected.size > 0) return;
    setViewerIndex(index);
  };

  const toggleSelect = (item: ProfileMediaItem) => {
    const key = keyFor(item);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const downloadOne = async (item: ProfileMediaItem) => {
    const key = keyFor(item);
    setDownloadingKey(key);
    setStatusMsg(null);
    try {
      const ext =
        item.type === 'photo'
          ? getImageExtension(item.downloadUrl)
          : 'mp4';
      const filename = `${sanitizeForFilename(
        `${item.author || 'x'}_${item.tweetId || 'media'}_${item.type}`
      )}.${ext}`;
      const method = await downloadMediaUrl(item.downloadUrl, filename);
      setStatusMsg(
        method === 'share'
          ? shareHintMessage(filename)
          : `Downloaded ${filename}`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadingKey(null);
    }
  };

  const downloadSelected = async () => {
    const selectedItems = Array.from(selected)
      .map((k) => indexByKey.get(k))
      .filter(Boolean) as ProfileMediaItem[];

    if (selectedItems.length === 0) return;

    setStatusMsg(null);
    setError(null);

    try {
      const zipItems = selectedItems.map((item, i) => {
        const ext =
          item.type === 'photo'
            ? getImageExtension(item.downloadUrl)
            : 'mp4';
        return {
          url: item.downloadUrl,
          filename: `${sanitizeForFilename(
            `${item.author || 'x'}_${item.tweetId || i}_${item.type}`
          )}.${ext}`,
        };
      });

      const handle = profile?.screen_name || 'account';
      const result = await downloadItemsAsZip(
        zipItems,
        `${handle}_selected_media.zip`,
        setZipProgress
      );
      setStatusMsg(
        result.saved < result.total
          ? `Saved ${result.saved} of ${result.total} files as ${result.zipName}`
          : `Saved ${result.saved} files as ${result.zipName}`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zip download failed');
    } finally {
      setZipProgress(null);
    }
  };

  const clearSelection = () => setSelected(new Set());

  const viewerItem =
    viewerIndex != null &&
    viewerIndex >= 0 &&
    viewerIndex < filteredItems.length
      ? filteredItems[viewerIndex]
      : null;
  const viewerDownloading =
    viewerItem != null && downloadingKey === keyFor(viewerItem);

  return (
    <Box
      sx={{
        // Layout already provides a maxWidth Container — avoid double gutters on mobile.
        width: '100%',
        py: { xs: 0, md: 1 },
        pb: selected.size > 0 ? { xs: 12, sm: 10 } : undefined,
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            color: '#E7E9EA',
            fontSize: { xs: '1.5rem', md: '2rem' },
          }}
        >
          Account media
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 560 }}>
          Browse a public profile&apos;s media, or use Extract all to pull the full timeline
          into a folder or ZIP in one guided flow.
        </Typography>

        <Paper
          component="form"
          onSubmit={handleSearch}
          elevation={0}
          sx={{
            p: 2,
            mb: 4,
            backgroundColor: '#16181C',
            border: '1px solid #2F3336',
            borderRadius: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { sm: 'flex-start' },
          }}
        >
          <TextField
            fullWidth
            label="Profile"
            placeholder="@NASA or https://x.com/NASA"
            value={handleInput}
            onChange={(e) => setHandleInput(e.target.value)}
            disabled={loading}
            helperText="Public accounts only · media timeline via FXTwitter"
            FormHelperTextProps={{ sx: { color: '#71767B', mx: 0, mt: 1 } }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !handleInput.trim()}
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 140 },
              height: 48,
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              flexShrink: 0,
            }}
          >
            {loading ? 'Loading' : 'Browse'}
          </Button>
        </Paper>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              mb: 2,
              alignItems: 'center',
              '& .MuiAlert-message': { flex: 1, minWidth: 0 },
              '& .MuiAlert-action': {
                pt: 0,
                alignItems: 'center',
                mr: 0.5,
              },
            }}
            action={
              !profile && (canAccountBack || canAccountForward) ? (
                <AccountNavButtons
                  canBack={canAccountBack}
                  canForward={canAccountForward}
                  onBack={goAccountBack}
                  onForward={goAccountForward}
                  size={32}
                />
              ) : undefined
            }
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                pr: 1,
              }}
            >
              {!profile && handleInput.trim() && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: '#E7E9EA', fontWeight: 600 }}
                >
                  {handleInput.trim().startsWith('@')
                    ? handleInput.trim()
                    : `@${handleInput.trim()}`}
                </Typography>
              )}
              <Typography component="span" variant="body2">
                {error}
              </Typography>
            </Box>
          </Alert>
        )}
        {statusMsg && (
          <Alert severity="success" onClose={() => setStatusMsg(null)} sx={{ mb: 2 }}>
            {statusMsg}
          </Alert>
        )}

        {profile ? (
          <ProfileHeader
            profile={profile}
            onExtractAll={() => setExtractOpen(true)}
            extractDisabled={loading}
            onOpenFollowers={() => setFollowListKind('followers')}
            onOpenFollowing={() => setFollowListKind('following')}
            canAccountBack={canAccountBack}
            canAccountForward={canAccountForward}
            onAccountBack={goAccountBack}
            onAccountForward={goAccountForward}
            onMentionClick={(screenName) => {
              setViewerIndex(null);
              visitAccount(screenName);
            }}
          />
        ) : (
          loading && <ProfileHeaderSkeleton />
        )}

        {(profile || items.length > 0) && (
          <MediaTypeFilter
            value={mediaFilter}
            onChange={setMediaFilterSafe}
            counts={filterCounts}
            disabled={loading && items.length === 0}
          />
        )}

        <MediaGrid
          items={filteredItems}
          selectedIds={selected}
          selectionMode={selected.size > 0}
          loading={loading}
          loadingMore={loadingMore && hasMore}
          scrollSentinelRef={hasMore ? scrollSentinelRef : undefined}
          onToggleSelect={toggleSelect}
          onOpen={openViewer}
          onDownload={downloadOne}
          downloadingKey={downloadingKey}
        />

        {!loading && items.length === 0 && !error && !profile && (
          <EmptyState
            icon={<PhotoLibraryIcon sx={{ fontSize: 28 }} />}
            title="Enter a handle above to load their public media gallery."
            subtitle="Try @NASA or paste a profile link."
          />
        )}

        {!loading && items.length === 0 && !error && profile && (
          <EmptyState
            icon={<PhotoLibraryIcon sx={{ fontSize: 28 }} />}
            title={`No downloadable media found on @${profile.screen_name}'s recent timeline.`}
            subtitle="This account may not have photos or videos in recent posts, or the timeline may be unavailable."
          />
        )}

        {!loading &&
          items.length > 0 &&
          filteredItems.length === 0 &&
          !error && (
            <EmptyState
              icon={<PhotoLibraryIcon sx={{ fontSize: 28 }} />}
              title={
                mediaFilter === 'video'
                  ? 'No videos in the loaded timeline.'
                  : 'No photos in the loaded timeline.'
              }
              subtitle="Try All, or scroll to load more posts — Extract all still pulls every media type."
            />
          )}

        {filteredItems.length > 0 && !hasMore && !loadingMore && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid #2F3336',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: '#71767B', letterSpacing: '0.02em' }}
            >
              End of timeline
            </Typography>
          </Box>
        )}
      </Box>

      <MediaLightbox
        open={viewerIndex != null}
        items={filteredItems}
        index={viewerIndex ?? 0}
        downloading={viewerDownloading}
        onClose={() => setViewerIndex(null)}
        onNavigate={setViewerIndex}
        onDownload={downloadOne}
        onMentionClick={(screenName) => {
          setViewerIndex(null);
          visitAccount(screenName);
        }}
      />

      {profile && (
        <ExtractAllModal
          open={extractOpen}
          profile={profile}
          seedItems={items}
          seedCursor={cursor}
          onClose={() => setExtractOpen(false)}
          onItemsDiscovered={(fresh, nextCursor) => {
            setItems((prev) => {
              const seen = new Set(prev.map((i) => `${i.tweetId}|${i.downloadUrl}`));
              const merged = [...prev];
              for (const item of fresh) {
                const key = `${item.tweetId}|${item.downloadUrl}`;
                if (!seen.has(key)) {
                  seen.add(key);
                  merged.push(item);
                }
              }
              return merged;
            });
            applyPageCursor(nextCursor, fresh.length, cursorRef.current);
          }}
          onComplete={(message) => setStatusMsg(message)}
        />
      )}

      {profile && followListKind && (
        <FollowListDialog
          open
          handle={profile.screen_name}
          kind={followListKind}
          onClose={() => setFollowListKind(null)}
          onSelectUser={(screenName) => {
            setFollowListKind(null);
            setViewerIndex(null);
            visitAccount(screenName);
          }}
        />
      )}

      <AnimatePresence>
        {selected.size > 0 && (
          <Box
            component={motion.div}
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
              px: { xs: 1.5, sm: 2 },
              pb: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              pointerEvents: 'none',
            }}
          >
            <Paper
              elevation={8}
              sx={{
                pointerEvents: 'auto',
                maxWidth: 720,
                mx: 'auto',
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                backgroundColor: '#16181C',
                border: '1px solid #2F3336',
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ flex: 1, minWidth: 90, fontWeight: 600, color: '#E7E9EA' }}
              >
                {selected.size} selected
              </Typography>
              {zipProgress && (
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <LinearProgress
                    variant="determinate"
                    value={
                      zipProgress.total
                        ? (zipProgress.current / zipProgress.total) * 100
                        : 0
                    }
                  />
                </Box>
              )}
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearSelection}
                sx={{ textTransform: 'none', color: 'text.secondary', minHeight: 40 }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={
                  zipProgress ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CloudDownloadIcon />
                  )
                }
                onClick={downloadSelected}
                disabled={!!zipProgress}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40,
                  flexGrow: { xs: 1, sm: 0 },
                }}
              >
                {`Download ZIP (${selected.size})`}
              </Button>
            </Paper>
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default GalleryPage;
